import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const AdminNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [activeUsers] = useState(1847);
  const [systemStatus] = useState('healthy');
  const [alertCount] = useState(2);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, userProfile } = useAuth();
  const profileMenuRef = useRef(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/institute-login');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef?.current && !profileMenuRef?.current?.contains(event?.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const quickActions = [
    {
      label: 'System Logs',
      icon: 'FileText',
      onClick: () => {}
    },
    {
      label: 'Reports',
      icon: 'Download',
      onClick: () => {}
    },
    {
      label: 'Settings',
      icon: 'Settings',
      onClick: () => {}
    }
  ];

  const getStatusColor = () => {
    switch (systemStatus) {
      case 'healthy':
        return 'bg-success';
      case 'warning':
        return 'bg-warning';
      case 'critical':
        return 'bg-error';
      default:
        return 'bg-muted-foreground';
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 h-[60px] bg-card shadow-md z-1000">
        <div className="max-w-[1440px] mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/admin-dashboard" className="flex items-center gap-3 transition-smooth hover-lift">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} color="var(--color-primary)" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">Studyexpo Admin</span>
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <div className="flex items-center gap-3 px-3 py-2 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                <span className="text-sm font-medium text-muted-foreground capitalize">
                  {systemStatus}
                </span>
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1">
                <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm font-medium data-text">{activeUsers?.toLocaleString()}</span>
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => {}}
            >
              <Icon name="Bell" size={20} />
              {alertCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                  {alertCount}
                </span>
              )}
            </Button>

            <div className="relative" ref={profileMenuRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              >
                <Icon name="MoreVertical" size={20} />
              </Button>
              {isProfileMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-medium text-foreground">Admin</p>
                    <p className="text-xs text-muted-foreground truncate">{userProfile?.email || ''}</p>
                  </div>
                  <button
                    onClick={() => {}}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                  >
                    <Icon name="Settings" size={16} />
                    Settings
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2 border-t border-border"
                  >
                    <Icon name="LogOut" size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </nav>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-[60px] bg-background z-1020 lg:hidden">
          <div className="flex flex-col p-6 gap-2">
            <div className="px-4 py-3 bg-muted rounded-lg mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">System Status</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                  <span className="text-sm font-medium capitalize">{systemStatus}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">Active Users:</span>
                <span className="text-sm font-medium data-text">{activeUsers?.toLocaleString()}</span>
              </div>
            </div>

            <Button
              variant="outline"
              fullWidth
              iconName="Bell"
              iconPosition="left"
              className="mb-3"
              onClick={() => {}}
            >
              Alerts
              {alertCount > 0 && (
                <span className="ml-auto w-6 h-6 bg-error text-error-foreground text-xs font-semibold rounded-full flex items-center justify-center">
                  {alertCount}
                </span>
              )}
            </Button>

            <div className="space-y-2">
              {quickActions?.map((action) => (
                <Button
                  key={action?.label}
                  variant="outline"
                  fullWidth
                  iconName={action?.icon}
                  iconPosition="left"
                  onClick={action?.onClick}
                >
                  {action?.label}
                </Button>
              ))}
              <Button
                variant="outline"
                fullWidth
                iconName="LogOut"
                iconPosition="left"
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminNavigation;